import { Box, Button, Stack, Theme, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import StartSigning from "assets/icons1/StartSigningIcon.svg";
import { isTruthy } from "helpers/methods";
import DocumentPreview from "../components/DocumentPreview";
import NeedVerificationContent from "./components/NeedVerificationContent";
import SignerCompletionCard from "./components/SignerCompletionCard";
import { CustomInput } from "global/components";
import { RequestParticipant } from "models/interfaces";

/** Shared cap for the decline reason / delegate private message fields. */
export const REASON_MAX_LENGTH = 500;

/**
 * "••••4567" for the phone-OTP "we texted a code to …" copy. The sign-request
 * read sends `authPhoneHint`, but the role read that fills the participant
 * returns the raw `authPhone` ({ countryCode, number }) instead, so mask its
 * last four digits the same way the backend's `Phone.maskedHint()` does.
 */
const getAuthPhoneHint = (participant: RequestParticipant) => {
  const security = participant.requestSecurity;
  if (security?.authPhoneHint) return security.authPhoneHint;
  const phone: any = security?.authPhone;
  const digits = String(
    (typeof phone === "string" ? phone : phone?.number) ?? "",
  ).replace(/\D/g, "");
  return digits.length >= 4 ? `••••${digits.slice(-4)}` : null;
};

/**
 * Human label for the floating field navigator, matching the design's
 * "Your name / List selection / Options / Signature" wording. Falls back to a
 * title-cased field type so a new backend type still reads sensibly.
 */
export const fieldNavLabel = (field?: { type?: string; name?: string }) => {
  if (!field) return "Fields";
  const labels: Record<string, string> = {
    [strings.name]: "Your name",
    [strings.email]: "Email",
    [strings.title]: "Job title",
    [strings.companyName]: "Company",
    [strings.text]: "Text",
    [strings.dateSigned]: "Date signed",
    date: "Date",
    signature: "Signature",
    initials: "Initials",
    checkbox: "Options",
    radio_button: "List selection",
    dropdown: "List selection",
  };
  const type = field.type ?? "";
  return (
    labels[type] ??
    type
      .split("_")
      .filter(Boolean)
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
      )
      .join(" ") ??
    "Field"
  );
};

const getHeaderText = (status: string) => {
  switch (status) {
    case strings.signed:
      return strings.requestStatusHeaderMessages.signed;
    case strings.expired:
      return strings.requestStatusHeaderMessages.expired;
    case strings.voided:
      return strings.requestStatusHeaderMessages.voided;
    case strings.waitingForOthers:
      return strings.requestStatusHeaderMessages.waitingForOthers;
    case strings.notYourTurnErrorMessageV1:
      return strings.requestStatusHeaderMessages.notYourTurnErrorMessageV1;
    case strings.notYourTurnErrorMessageV2:
      return strings.requestStatusHeaderMessages.notYourTurnErrorMessageV2;
    case strings.nonAssociatedErrorMessage:
      return strings.requestStatusHeaderMessages.nonAssociatedErrorMessage;
    case strings.declined:
      return strings.requestStatusHeaderMessages.declined;
    case strings.rejected:
      return strings.requestStatusHeaderMessages.rejected;
    case strings.verificationLocked:
      return strings.requestStatusHeaderMessages.verificationLocked;
    case strings.requestDelegatedMessage:
      return strings.requestStatusHeaderMessages.requestDelegatedMessage;
    case strings.approved:
      return strings.requestStatusHeaderMessages.approved;
  }
};

const getBodyText = (
  status: string,
  declinerName?: string,
  declinerEmail?: string,
) => {
  switch (status) {
    case strings.signed:
      return strings.requestStatusBodyMessages().signed;
    case strings.expired:
      return strings.requestStatusBodyMessages().expired;
    case strings.voided:
      return strings.requestStatusBodyMessages().voided;
    case strings.waitingForOthers:
      return strings.requestStatusBodyMessages().waitingForOthers;
    case strings.declined:
      return `${strings.requestStatusBodyMessages().declined} ${declinerName} (${declinerEmail})`;
    case strings.rejected:
      return `${strings.requestStatusBodyMessages().rejected} ${declinerName} (${declinerEmail})`;
    case strings.verificationLocked:
      return strings.requestStatusBodyMessages().verificationLocked;
    case strings.approved:
      return strings.alreadyApprovedMessage;
  }
};

const getActionButtons = (
  reqToken: string,
  status: string,
  loading: boolean,
  setReasonOpen: (value: boolean) => void,
  setDialogType: (value: string) => void,
  docUrl: string,
  onStartSigning: () => void,
  allowDelegation: boolean,
  docLoading: boolean,
) => {
  switch (status) {
    case strings.inReview:
      return (
        <Button
          disabled={docLoading}
          variant="contained"
          color="error"
          size="small"
          startIcon={<img src={StartSigning} alt="" aria-hidden="true" />}
          onClick={onStartSigning}
          sx={{
            background: "primary.main",
            textTransform: "none",
            color: "white",
            borderColor: "primary.main",
            "&:hover": { background: "primary.main" },
          }}
          aria-label="Review request"
        >
          Review request
        </Button>
      );
    case "ccView":
      if (docLoading) return null;
      return (
        <Button
          disabled={docLoading}
          variant="contained"
          size="small"
          onClick={onStartSigning}
          sx={{
            textTransform: "none",
            background: "primary.main",
            color: "white",
            "&:hover": { background: "primary.main" },
          }}
          aria-label="View request"
        >
          View Request
        </Button>
      );
    case "ccView":
      if (docLoading) return null;
      return (
        <Button
          disabled={docLoading}
          variant="contained"
          size="small"
          onClick={onStartSigning}
          sx={{
            textTransform: "none",
            background: "#00A1FF",
            color: "white",
            "&:hover": { background: "#00A1FF" },
          }}
          aria-label="View request"
        >
          View Request
        </Button>
      );
    default:
      return null;
  }
};

/** Title for the decline / delegate prompt. */
export const reasonHeader = (dialogType: string) =>
  dialogType === strings.decline ? "Decline Request" : "Delegate Request";

export const reasonContent = (
  dialogType: string,
  reason: string,
  setReason: (value: string) => void,
  reasonError: string,
  setReasonError: (value: string) => void,
  delegationEmail: string,
  setDelegationEmail: (value: string) => void,
  delegationEmailError: string,
  setDelegationEmailError: (value: string) => void,
  delegationName: string,
  setDelegationName: (value: string) => void,
  delegationNameError: string,
  setDelegationNameError: (value: string) => void,
) => {
  return (
    // The Modal System's body is left-aligned and already inset, so the fields
    // no longer need the centring wrapper the old shell required.
    <Box>
      <Stack direction="column" spacing={2}>
        {dialogType === strings.delegate && (
          <>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "signer.text.meta",
                lineHeight: 1.6,
              }}
            >
              Pass the signing responsibility to someone else. The requester
              will be notified of the change.
            </Typography>
            <CustomInput
              required
              id="delegationName"
              placeHolder="Enter Signer Name"
              type="text"
              name="delegationName"
              label="Signer Name"
              value={delegationName}
              onChange={(e: any) => {
                setDelegationName(e.target.value);
                if (isTruthy(e.target.value.trim())) setDelegationNameError("");
              }}
              error={delegationNameError}
              helperText={delegationNameError}
            />
            <CustomInput
              required
              id="delegationEmail"
              placeHolder="Enter Signer Email"
              type="text"
              name="delegationEmail"
              label="Signer Email"
              value={delegationEmail}
              onChange={(e: any) => {
                const nextValue = e.target.value;
                setDelegationEmail(nextValue);

                const trimmed = nextValue.trim();
                if (!isTruthy(trimmed)) {
                  setDelegationEmailError("");
                  return;
                }

                // Disallow comma-separated emails; only one valid email is allowed.
                if (trimmed.includes(",")) {
                  setDelegationEmailError("Please enter a single valid email");
                  return;
                }

                setDelegationEmailError(
                  strings.regex.test(trimmed)
                    ? ""
                    : "Please enter a valid email",
                );
              }}
              error={delegationEmailError}
              helperText={delegationEmailError}
            />
          </>
        )}
        <CustomInput
          required={dialogType === strings.decline}
          id="reason"
          placeHolder={
            dialogType === strings.decline
              ? "Enter reason"
              : "Enter private message"
          }
          type="text"
          name="reason"
          label={dialogType === strings.decline ? "Reason" : "Private Message"}
          value={reason}
          propsToInputElement={{ maxLength: REASON_MAX_LENGTH }}
          onChange={(e: any) => {
            setReason(e.target.value);
            if (isTruthy(e.target.value.trim())) setReasonError("");
          }}
          error={reasonError}
          helperText={reasonError}
        />
        {dialogType === strings.delegate && !reasonError && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "signer.text.meta",
              textAlign: "right",
              mt: -1.5,
            }}
          >
            {reason.length} / {REASON_MAX_LENGTH}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export const reasonFooter = (
  dialogType: string,
  handleDialogClose: (value: boolean) => void,
  getOnClickFunction: () => void,
  setDelegationEmail: (value: string) => void,
  setDelegationName: (value: string) => void,
  setReason: (value: string) => void,
  setReasonError: (value: string) => void,
  setDelegationEmailError: (value: string) => void,
  setDelegationNameError: (value: string) => void,
  isSubmitDisabled: boolean = false,
) => {
  // Rendered into the Modal System footer's action slot, so the bar, its
  // divider and its alignment come from the shell — only the two buttons are
  // defined here, because the signer surface keeps its own purple / danger
  // fills rather than the app-wide brand red.
  return (
    <>
      <Button
        variant="outlined"
        onClick={() => {
          handleDialogClose(false);
          setDelegationEmail("");
          setDelegationName("");
          setReason("");
          setReasonError("");
          setReason("");
          setDelegationEmailError("");
          setDelegationNameError("");
        }}
      >
        {strings.cancel}
      </Button>
      <Button
        variant="contained"
        disabled={isSubmitDisabled}
        onClick={() => {
          getOnClickFunction();
        }}
        sx={{
          textTransform: "capitalize",
          boxShadow: "none",
          // Delegate is the purple secondary action, decline the red
          // destructive one — matching the modal buttons in the design.
          backgroundColor:
            dialogType === strings.delegate
              ? "signer.purple.main"
              : "signer.danger.main",
          "&:hover": {
            boxShadow: "none",
            backgroundColor:
              dialogType === strings.delegate
                ? "signer.purple.dark"
                : "signer.danger.dark",
          },
        }}
      >
        {dialogType}
      </Button>
    </>
  );
};

export const getCardContent = (
  reqToken: string,
  theme: Theme,
  isMobile: boolean,
  status: string,
  downloadDocs: () => void,
  loading: boolean,
  setReasonOpen: (value: boolean) => void,
  setDialogType: (value: string) => void,
  docUrl: string,
  onStartSigning: () => void,
  allowDelegation: boolean,
  docLoading: boolean,
  setDocLoading: Function,
  errorMessage?: string,
  selectedParticipant?: RequestParticipant,
  setLoading?: (v: boolean) => void,
  onVerificationSuccess?: () => void | Promise<void>,
  declinerName?: string,
  declinerEmail?: string,
  isDownloadingZip?: boolean,
  requesterName?: string,
  requesterEmail?: string,
  signedDocName?: string,
  requestId?: string,
  allowDownload: boolean = true,
) => {
  switch (status) {
    case strings.signed:
    case strings.expired:
    case strings.voided:
    case strings.waitingForOthers:
    case strings.nonAssociatedErrorMessage:
    case strings.notYourTurnErrorMessageV1:
    case strings.notYourTurnErrorMessageV2:
    case strings.requestDelegatedMessage:
    case strings.declined:
    case strings.rejected:
    case strings.verificationLocked:
    case strings.approved:
      return (
        <SignerCompletionCard
          status={status}
          isMobile={isMobile}
          headerText={getHeaderText(status)}
          bodyText={
            isTruthy(errorMessage)
              ? errorMessage
              : getBodyText(status, declinerName, declinerEmail)
          }
          downloadDocs={downloadDocs}
          isDownloadingZip={isDownloadingZip}
          requesterName={requesterName}
          requesterEmail={requesterEmail}
          signedDocName={signedDocName}
          signerName={selectedParticipant?.name}
          signerEmail={selectedParticipant?.email}
          requestId={requestId}
          declinerName={declinerName}
          declinedBySelf={
            !!declinerEmail &&
            !!selectedParticipant?.email &&
            declinerEmail.toLowerCase() ===
              selectedParticipant.email.toLowerCase()
          }
          allowDownload={allowDownload}
        />
      );
    case "needVerification":
      return selectedParticipant && setLoading && onVerificationSuccess ? (
        <NeedVerificationContent
          needOTPValidation={
            selectedParticipant.requestSecurity?.needOTPValidation ?? false
          }
          needPhoneValidation={
            selectedParticipant.requestSecurity?.needPhoneValidation ?? false
          }
          authPhoneHint={getAuthPhoneHint(selectedParticipant)}
          reqToken={reqToken}
          loading={loading}
          setLoading={setLoading}
          onVerificationSuccess={onVerificationSuccess}
          selectedParticipant={selectedParticipant}
        />
      ) : (
        <Box />
      );

    case strings.pending:
    case strings.inReview:
    case "ccView":
      return (
        <>
          {!loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                overflowX: "hidden",
              }}
            >
              <DocumentPreview
                docUrl={docUrl!}
                docLoading={docLoading}
                setDocLoading={setDocLoading}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "stretch",
                  gap: 1.5,
                  width: "100%",
                  maxWidth: 360,
                  mx: "auto",
                  mt: 1,
                }}
              >
                {getActionButtons(
                  reqToken,
                  status,
                  loading,
                  setReasonOpen,
                  setDialogType,
                  docUrl,
                  onStartSigning,
                  allowDelegation,
                  docLoading,
                )}
              </Box>
            </Box>
          )}
        </>
      );
  }
};
